import { endOfDay, startOfDay } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Can, DatePicker, NavBarSearch, NavBarSearchAppointments, NavPortal, OverlayLoader, SkeletonTable, StatusFilters, statusFilters } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useSearchTerm } from "components/NavBarSearch/hooks";
import { usePermanentRealTimeUser, useRegisterNavAction } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, ROLES, STATUS_IDENTIFIERS } from "models";
import "modules/Appointments/Appointments.scss";
import { AppointmentTable, Timer } from "modules/Appointments/components";
import { CreateAppointment } from "modules/Appointments/components/NewAppointment";
import {
  setCarMakesOptions,
  setSelectedDate,
  setSelectedDates,
  setSelectedIdentifiersToFilter,
  useCarMakesToFilter,
  useCartIndicatorsToFilter,
  useRealTimeAppointmentsQuery,
  useSelectedDate,
  useSelectedDates,
  useSelectedIdentifiersToFilter,
  useSpecialIndicatorsToFilter,
  useStarIndicatorsToFilter
} from "modules/Appointments/hooks";
import { APPOINTMENTS_STORAGE_KEYS } from "modules/Appointments/storageKeys";
import { filterAppointmentsBySearch, filterSpecialIndicators, sortAppointments } from "modules/Appointments/util";
import { isCarReady, isQualityCheck } from "utils/appointmentUtils";
import { PAGE_SIZE, getPreference, setPreference } from "utils/common";
import { isSameDayHelper, toDate } from "utils/dateHelperFunctions";

export enum WO_FILTERS {
  ALL = "All",
  WO = "WO",
  INTERNAL = "Internal"
}

export enum DATE_ORDER_BY {
  APPOINTMENT_DATE_ASCENDING = "time_car_app_asc",
  APPOINTMENT_DATE_DESCENDING = "time_car_app_desc",
  SCHEDULED_OUT_DATE_ASCENDING = "car_return_time_asc",
  SCHEDULED_OUT_DATE_DESCENDING = "car_return_time_desc"
}

const EXCLUDED_NUMBER_OF_DAYS = 7;

export const Appointments = () => {
  const selectedDates = useSelectedDates();
  const { user } = usePermanentRealTimeUser();
  const [woColumnAccessor, setWoColumnAccessor] = useState(getPreference(APPOINTMENTS_STORAGE_KEYS.WoPreference, WO_FILTERS.ALL));
  const [dateColumnAccessor, setDateColumnAccessor] = useState(
    getPreference(
      APPOINTMENTS_STORAGE_KEYS.DateSortPreference,
      user?.list_appointments_by_scheduled_range ? DATE_ORDER_BY.SCHEDULED_OUT_DATE_ASCENDING : DATE_ORDER_BY.APPOINTMENT_DATE_ASCENDING
    )
  );
  const [startDate, setStartDate] = useState<Date | null>(selectedDates.startDate ? startOfDay(new Date(selectedDates.startDate)) : startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date | null>(selectedDates.endDate ? endOfDay(new Date(selectedDates.endDate)) : endOfDay(new Date()));

  const containerRef = useRef<HTMLDivElement | null>(null);

  const { data: appointments = [], isLoading, isFetching, refetch: refetchAppointments } = useRealTimeAppointmentsQuery({ driveFetch: false });
  const { selectedLocation } = useSelectedLocation();
  const selectedStatusIdentifiersFilters = useSelectedIdentifiersToFilter();

  const searchTerm = useSearchTerm();

  const carMakesToFilter = useCarMakesToFilter();

  const cartIndicators = useCartIndicatorsToFilter();
  const starIndicators = useStarIndicatorsToFilter();
  const specialIndicators = useSpecialIndicatorsToFilter();

  const date = useSelectedDate();
  const isTodaysList = isSameDayHelper(date, new Date());

  useEffect(() => {
    const carMakeValues = appointments.map(({ car_make }) => car_make);
    setCarMakesOptions([...new Set(carMakeValues)]);
  }, [appointments]);

  useEffect(() => {
    if (getPreference<boolean>(APPOINTMENTS_STORAGE_KEYS.StatusIdentifierPreference, false)) return;

    const appointmentStatuses = user?.appointment_status_identifiers
      ? statusFilters.map(status => ({ ...status, isActive: (user.appointment_status_identifiers ?? []).includes(status.identifier) }))
      : statusFilters;

    setSelectedIdentifiersToFilter(appointmentStatuses);
  }, [user]);

  useRegisterNavAction(refetchAppointments);

  const isSelectingDateRange = user?.role_id && ![ROLES.CallCenter, ROLES.WarrantyManager].includes(user.role_id);

  useEffect(() => {
    if (isSelectingDateRange) {
      const start = selectedDates.startDate ? new Date(selectedDates.startDate) : new Date();
      const end = selectedDates.endDate ? new Date(selectedDates.endDate) : new Date();
      setStartDate(startOfDay(start));
      setEndDate(endOfDay(end));
    }
  }, [selectedDates, isSelectingDateRange]);

  const handleDateChange = (...dates: (Date | null)[]) => {
    if (!isSelectingDateRange) {
      const [date] = dates;
      if (!date) return;
      setSelectedDate(date);
      return;
    }

    const [start, end] = dates;

    const isRange = startDate && endDate;
    const isNewSelection = start && !end && isRange;

    if (isNewSelection) {
      setStartDate(startOfDay(start));
      setEndDate(null);
    } else {
      setStartDate(start ? startOfDay(start) : null);
      setEndDate(end ? endOfDay(end) : null);
    }

    if (start && end) {
      setSelectedDate(startOfDay(start));
      setSelectedDates([start, end]);
    }
  };

  const handleClearDate = () => {
    const today = new Date();
    setStartDate(startOfDay(today));
    setEndDate(endOfDay(today));
    setSelectedDate(startOfDay(today));
    setSelectedDates([today, today]);
  };

  const filteredAppointments = useMemo(() => {
    const selectedIdentifiers = selectedStatusIdentifiersFilters.filter(filter => !filter.isActive).map(s => s.identifier);
    let filteredList = appointments.filter(app => !selectedIdentifiers.includes(app.appointment_status_identifier));
    filteredList = filterSpecialIndicators(filteredList, { ...specialIndicators, ...cartIndicators, ...starIndicators });

    if (selectedIdentifiers.includes(STATUS_IDENTIFIERS.CarOutOfShop)) filteredList = filteredList.filter(app => !app.car_out_of_shop);

    if (searchTerm) filteredList = filterAppointmentsBySearch(filteredList, searchTerm as string);

    if (carMakesToFilter.length > 0) filteredList = filteredList.filter(({ car_make }) => carMakesToFilter.includes(car_make));

    if (selectedIdentifiers.includes(STATUS_IDENTIFIERS.NewCarStatus))
      filteredList = filteredList.filter(({ appointment_status_identifier }) => appointment_status_identifier !== STATUS_IDENTIFIERS.CheckInDone);

    if (selectedIdentifiers.includes(STATUS_IDENTIFIERS.CarReadyStatus))
      filteredList = filteredList.filter(({ appointment_status_identifier }) => !isCarReady(appointment_status_identifier));

    if (selectedIdentifiers.includes(STATUS_IDENTIFIERS.QualityCheckStatus))
      filteredList = filteredList.filter(({ appointment_status_identifier }) => !isQualityCheck(appointment_status_identifier));

    if (woColumnAccessor !== WO_FILTERS.ALL) {
      const showInternal = woColumnAccessor === WO_FILTERS.INTERNAL;
      filteredList = filteredList.filter(app => showInternal === app.internal);
    }
    if (user?.car_type_filter) {
      filteredList = filteredList.filter(appointment => appointment.vehicle_type === user.car_type_filter);
    }

    return sortAppointments(filteredList, selectedLocation?.statuses);
  }, [
    appointments,
    selectedStatusIdentifiersFilters,
    searchTerm,
    specialIndicators,
    cartIndicators,
    starIndicators,
    carMakesToFilter,
    selectedLocation?.statuses,
    woColumnAccessor,
    dateColumnAccessor,
    user?.car_type_filter
  ]);

  const handleChangeWoColumnAccessor = useCallback((value: WO_FILTERS) => {
    setPreference(APPOINTMENTS_STORAGE_KEYS.WoPreference, value as WO_FILTERS);
    setWoColumnAccessor(value);
  }, []);

  const handleChangeDateColumnAccessor = useCallback((value: DATE_ORDER_BY) => {
    setPreference(APPOINTMENTS_STORAGE_KEYS.DateSortPreference, value);
    setDateColumnAccessor(value);
  }, []);

  if (isLoading || isFetching)
    return (
      <>
        <OverlayLoader />
        <SkeletonTable columnSize={13} pageSize={PAGE_SIZE.PageSize25} />
      </>
    );

  return (
    <div ref={containerRef} className="AppointmentsTableContainer">
      <NavPortal>
        <NavBarSearch>
          <NavBarSearchAppointments />
        </NavBarSearch>
      </NavPortal>

      <div className="AppointmentsInnerContainer">
        <div className="AppointmentActions">
          <div className="AppointmentActions-wrapper">
            {selectedLocation?.id && selectedLocation.create_wo_is_enabled && (
              <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Appointments}>
                <Can I={ACL_ACTIVITIES.List} the={ACL_RESOURCES.AppointmentWONrCounter}>
                  <CreateAppointment />
                </Can>
              </Can>
            )}
            <Timer />
          </div>

          <div className="AppointmentActions-wrapper">
            {isSelectingDateRange ? (
              <DatePicker
                onDateChange={handleDateChange}
                selectsRange
                startDate={startDate}
                endDate={endDate}
                clearable
                maxRangeDays={EXCLUDED_NUMBER_OF_DAYS}
                showLabel
                onClear={handleClearDate}
              />
            ) : (
              <DatePicker onDateChange={handleDateChange} date={toDate(date)} showLabel />
            )}
            <StatusFilters filters={selectedStatusIdentifiersFilters} onChange={setSelectedIdentifiersToFilter} />
          </div>
        </div>
        <div className="AppointmentsTableWrapper">
          <AppointmentTable
            containerRef={containerRef}
            appointments={filteredAppointments}
            isTodaysList={isTodaysList}
            woColumnAccessor={woColumnAccessor}
            dateColumnAccessor={dateColumnAccessor}
            handleChangeWoColumnAccessor={handleChangeWoColumnAccessor}
            handleChangeDateColumnAccessor={handleChangeDateColumnAccessor}
          />
        </div>
      </div>
    </div>
  );
};
